// src/utils/mockDataSubcontracts.ts
// Phase 1 — G07 / R11 + G08 / R12

export type SubcontractStatus = 'DRAFT' | 'EXECUTED' | 'ACTIVE' | 'PRACTICAL_COMPLETION' | 'DEFECTS' | 'COMPLETE' | 'TERMINATED'
export type WHSPrequalStatus = 'APPROVED' | 'PENDING' | 'EXPIRED' | 'NOT_REQUIRED'
export type BCIPAStatus = 'COMPLIANT' | 'CLAIM_DUE' | 'RESPONSE_OVERDUE' | 'ENDORSEMENT_REQUIRED'

export interface SubcontractMilestone {
  id: string; description: string; dueDate: string; completedDate?: string
  claimAmount: number; retentionWithheld: number; amountPayable: number
  paid: boolean; paidAt?: string; invoiceRef?: string
}

export interface Subcontract {
  id: string; reference: string; jobId: string; jobRef: string
  subcontractorId: string; subcontractorName: string; subcontractorABN: string
  description: string; contractValue: number; retentionPct: number
  retentionHeld: number; retentionReleased: number
  headContractRef: string | null; bcipaClause: string | null
  bcipaStatus: BCIPAStatus; nextClaimDueDate: string | null
  whsPrequalStatus: WHSPrequalStatus; whsPrequalExpiryDate: string | null
  qbccLicenceRequired: boolean; qbccLicenceNumber: string | null
  qbccDisclosureRequired: boolean; milestones: SubcontractMilestone[]
  status: SubcontractStatus; executedAt: string | null
  practicalCompletionDate: string | null; defectsLiabilityPeriodDays: number
  dlpExpiryDate: string | null; notes: string | null
  createdById: string; createdByName: string; createdAt: string
}

export const MOCK_SUBCONTRACTS: Subcontract[] = [
  {
    id: 'sc-001', reference: 'SC-2026-001', jobId: 'j1', jobRef: 'TP-2026-001',
    subcontractorId: 'sub-tc-01', subcontractorName: 'SafeTraffic QLD Pty Ltd',
    subcontractorABN: '44 123 456 789',
    description: 'Traffic control services — Sandgate Rd Signal Upgrade. Provision of licensed TC officers and traffic management implementation per approved TMP. Night-time works required for Stages 2 and 3.',
    contractValue: 48_500, retentionPct: 5, retentionHeld: 4_850, retentionReleased: 0,
    headContractRef: 'TMR-QLD-2026-039-TP001',
    bcipaClause: 'cl. 14 — payment within 25 business days of payment claim',
    bcipaStatus: 'CLAIM_DUE', nextClaimDueDate: '2026-07-25',
    whsPrequalStatus: 'APPROVED', whsPrequalExpiryDate: '2027-03-31',
    qbccLicenceRequired: true, qbccLicenceNumber: 'QBCC-TC-09441', qbccDisclosureRequired: false,
    status: 'ACTIVE', executedAt: '2026-05-01T00:00:00Z', practicalCompletionDate: null,
    defectsLiabilityPeriodDays: 0, dlpExpiryDate: null,
    notes: 'Back-to-back with TMR head contract traffic management provisions.',
    createdById: 'u2', createdByName: 'Jane Smith', createdAt: '2026-04-28T09:00:00Z',
    milestones: [
      { id: 'ms-001-1', description: 'Stage 1 — Kingsford Smith Dr closure (nights 1–4)', dueDate: '2026-05-31', completedDate: '2026-05-30', claimAmount: 14_200, retentionWithheld: 710, amountPayable: 13_490, paid: true, paidAt: '2026-06-18', invoiceRef: 'STQLD-INV-0412' },
      { id: 'ms-001-2', description: 'Stage 2 — Lutwyche Rd approaches (nights 5–9)', dueDate: '2026-06-30', completedDate: '2026-06-28', claimAmount: 17_300, retentionWithheld: 865, amountPayable: 16_435, paid: false },
      { id: 'ms-001-3', description: 'Stage 3 — Final commissioning night and clean-up', dueDate: '2026-08-15', claimAmount: 17_000, retentionWithheld: 850, amountPayable: 16_150, paid: false },
    ],
  },
  {
    id: 'sc-002', reference: 'SC-2026-002', jobId: 'j3', jobRef: 'TP-2026-003',
    subcontractorId: 'sub-surv-01', subcontractorName: 'Sunshine Survey Group Pty Ltd',
    subcontractorABN: '78 009 111 223',
    description: 'Survey services — Maroochydore Rd Intersection Reconstruction. Set-out, as-constructed and final survey.',
    contractValue: 22_400, retentionPct: 5, retentionHeld: 560, retentionReleased: 0,
    headContractRef: null, bcipaClause: 'cl. 14 — 25 business days from receipt of payment claim',
    bcipaStatus: 'COMPLIANT', nextClaimDueDate: '2026-08-01',
    whsPrequalStatus: 'APPROVED', whsPrequalExpiryDate: '2026-12-31',
    qbccLicenceRequired: false, qbccLicenceNumber: null, qbccDisclosureRequired: false,
    status: 'ACTIVE', executedAt: '2026-06-01T00:00:00Z', practicalCompletionDate: null,
    defectsLiabilityPeriodDays: 365, dlpExpiryDate: null,
    notes: 'Licensed surveyor sign-off required on all as-constructed drawings.',
    createdById: 'u2', createdByName: 'Jane Smith', createdAt: '2026-05-28T10:00:00Z',
    milestones: [
      { id: 'ms-002-1', description: 'Set-out survey — signal pole foundations and pit locations', dueDate: '2026-06-20', completedDate: '2026-06-19', claimAmount: 8_400, retentionWithheld: 420, amountPayable: 7_980, paid: true, paidAt: '2026-07-10', invoiceRef: 'SSG-0061' },
      { id: 'ms-002-2', description: 'As-constructed survey — conduit, pits, and kerb', dueDate: '2026-09-30', claimAmount: 9_200, retentionWithheld: 460, amountPayable: 8_740, paid: false },
      { id: 'ms-002-3', description: 'Final as-constructed plan — signal heads and controllers', dueDate: '2026-11-30', claimAmount: 4_800, retentionWithheld: 240, amountPayable: 4_560, paid: false },
    ],
  },
  {
    id: 'sc-003', reference: 'SC-2026-003', jobId: 'j3', jobRef: 'TP-2026-003',
    subcontractorId: 'sub-civil-01', subcontractorName: 'Coastal Civil Works Pty Ltd',
    subcontractorABN: '55 311 876 002',
    description: 'Civil earthworks, kerb and gutter reconstruction, and pavement patching — Maroochydore Rd Intersection.',
    contractValue: 198_000, retentionPct: 10, retentionHeld: 0, retentionReleased: 0,
    headContractRef: 'SUNSHINE-CC-2026-TP003',
    bcipaClause: 'cl. 14 — 25 business days. Progress claims on last business day of each month.',
    bcipaStatus: 'COMPLIANT', nextClaimDueDate: '2026-07-31',
    whsPrequalStatus: 'PENDING', whsPrequalExpiryDate: null,
    qbccLicenceRequired: true, qbccLicenceNumber: null, qbccDisclosureRequired: true,
    status: 'DRAFT', executedAt: null, practicalCompletionDate: null,
    defectsLiabilityPeriodDays: 365, dlpExpiryDate: null,
    notes: 'QBCC licence and SWMS required before execution. WHS prequalification file outstanding.',
    createdById: 'u4', createdByName: 'Sarah Lee', createdAt: '2026-07-10T09:00:00Z',
    milestones: [
      { id: 'ms-003-1', description: 'Mobilisation and saw-cutting — all kerb lines', dueDate: '2026-08-31', claimAmount: 42_000, retentionWithheld: 4_200, amountPayable: 37_800, paid: false },
      { id: 'ms-003-2', description: 'Earthworks and sub-base compaction', dueDate: '2026-09-30', claimAmount: 68_000, retentionWithheld: 6_800, amountPayable: 61_200, paid: false },
      { id: 'ms-003-3', description: 'Kerb and gutter construction and pavement reinstatement', dueDate: '2026-10-31', claimAmount: 88_000, retentionWithheld: 8_800, amountPayable: 79_200, paid: false },
    ],
  },
]

export const SUBCONTRACT_STATUS_META: Record<SubcontractStatus, { label: string; bg: string; text: string; icon: string }> = {
  DRAFT:                { label: 'Draft',          bg: 'bg-gray-100',  text: 'text-gray-600',  icon: '○' },
  EXECUTED:             { label: 'Executed',       bg: 'bg-blue-100',  text: 'text-blue-700',  icon: '✓' },
  ACTIVE:               { label: 'Active',         bg: 'bg-green-100', text: 'text-green-700', icon: '●' },
  PRACTICAL_COMPLETION: { label: 'PC achieved',    bg: 'bg-teal-100',  text: 'text-teal-700',  icon: '◆' },
  DEFECTS:              { label: 'Defects period', bg: 'bg-amber-100', text: 'text-amber-700', icon: '△' },
  COMPLETE:             { label: 'Complete',       bg: 'bg-gray-100',  text: 'text-gray-500',  icon: '✔' },
  TERMINATED:           { label: 'Terminated',     bg: 'bg-red-100',   text: 'text-red-600',   icon: '✕' },
}

export const BCIPA_STATUS_META: Record<BCIPAStatus, { label: string; bg: string; text: string; border: string }> = {
  COMPLIANT:            { label: 'BCIPA compliant',      bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200' },
  CLAIM_DUE:            { label: 'Claim due',            bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200' },
  RESPONSE_OVERDUE:     { label: 'Response overdue',     bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200' },
  ENDORSEMENT_REQUIRED: { label: 'Endorsement required', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
}

export const getTotalRetentionHeld    = () => MOCK_SUBCONTRACTS.reduce((s, sc) => s + sc.retentionHeld, 0)
export const getTotalSubcontractValue = () => MOCK_SUBCONTRACTS.reduce((s, sc) => s + sc.contractValue, 0)
export const getSubcontractsAwaitingWHS = () => MOCK_SUBCONTRACTS.filter(sc => sc.whsPrequalStatus === 'PENDING' || sc.whsPrequalStatus === 'EXPIRED').length
export const getBCIPAIssues = () => MOCK_SUBCONTRACTS.filter(sc => sc.bcipaStatus === 'RESPONSE_OVERDUE' || sc.bcipaStatus === 'CLAIM_DUE').length
